import type {
  MessageJobContext,
  MessageMatchData,
  MessageMetadata,
  ResumeContent,
  RevisionReadyEvent,
  TemplateId,
} from '@/types'
import type { LearningContentEvent, LearningTopicGeneratingEvent } from '@/types/learning'
import { create } from 'zustand'

// Resume preview data attached to assistant messages when AI shows preview
export interface ResumePreviewData {
  document_id: number
  focus_areas: string[]
  message_id: number
}

// Extended Message interface for local state (API Message + UI-specific fields)
export interface ChatMessage {
  id: string | number
  content: string
  role: 'user' | 'assistant'
  created_at: string
  msg_metadata?: MessageMetadata | null
  // UI-specific transient fields (not persisted)
  isNew?: boolean
  isStreaming?: boolean
  error?: boolean
  isRateLimitError?: boolean
  revision?: RevisionReadyEvent
  matchData?: MessageMatchData
  jobContext?: MessageJobContext
  resumePreview?: ResumePreviewData
  isGeneratingResume?: boolean
  statusText?: string
}

// Message display types for component mapping
export type MessageDisplayType = 'streaming' | 'resume_upload' | 'error' | 'assistant' | 'user'

interface ChatState {
  // Core chat state
  chatId: string | null
  messages: ChatMessage[]
  isSending: boolean

  // Resume preview panel state
  previewPanelOpen: boolean
  resumeContent: ResumeContent | null
  originalResumeContent: ResumeContent | null
  selectedTemplate: TemplateId
  userDocumentId: number | null
  isGeneratingRevision: boolean
  isUpdatingPreview: boolean
  previewJobTitle: string | undefined
  previewCompany: string | undefined
  previewPdfUrl: string | null
  previewIsAgentTailored: boolean
  previewLoading: boolean

  // Scroll anchor (persists across coach panel unmounts/tab switches)
  scrollDistanceFromBottom: number
  scrollWasAtBottom: boolean

  // Incremented when an external surface (e.g. Opportunities "reach out") sends
  // a coach message — drives pulse highlight and gentle scroll-in.
  coachFeedbackToken: number

  /** Pulses coach header until user acknowledges (same pattern as sidebar "Done" agents). */
  coachAttentionActive: boolean

  // Opens the mobile coach overlay (Opportunities has no side panel on small screens).
  mobileCoachOpenToken: number
  /** Bumped to move focus into the coach input, e.g. from Home's "Ask the Pelgo coach". */
  coachInputFocusToken: number

  // Learning panel state
  learningContent: LearningContentEvent | null
  learningPanelOpen: boolean
  isLoadingLearning: boolean
  learningError: string | null
  learningTopicGenerating: LearningTopicGeneratingEvent | null

  // Actions
  setChatId: (id: string | null) => void
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void
  addMessage: (message: ChatMessage) => void
  updateMessage: (id: string | number, updates: Partial<ChatMessage>) => void
  setIsSending: (sending: boolean) => void

  // Resume preview actions
  setPreviewPanelOpen: (open: boolean) => void
  setResumeContent: (content: ResumeContent | null) => void
  setOriginalResumeContent: (content: ResumeContent | null) => void
  setSelectedTemplate: (template: TemplateId) => void
  setUserDocumentId: (id: number | null) => void
  setIsGeneratingRevision: (generating: boolean) => void
  setIsUpdatingPreview: (updating: boolean) => void
  setPreviewJobContext: (jobTitle?: string, company?: string) => void
  setPreviewPdfUrl: (url: string | null) => void
  setPreviewIsAgentTailored: (tailored: boolean) => void
  setPreviewLoading: (loading: boolean) => void
  clearResumePreviewContext: () => void

  // Scroll position actions
  setScrollAnchor: (distanceFromBottom: number, wasAtBottom: boolean) => void

  // Highlight coach + gently scroll new activity into view (external triggers).
  requestCoachFeedback: () => void

  requestMobileCoachOpen: () => void
  requestCoachInputFocus: () => void

  clearCoachAttention: () => void

  // Learning panel actions
  setLearningContent: (
    content:
      | LearningContentEvent
      | null
      | ((prev: LearningContentEvent | null) => LearningContentEvent | null),
  ) => void
  setLearningPanelOpen: (open: boolean) => void
  setIsLoadingLearning: (loading: boolean) => void
  setLearningError: (error: string | null) => void
  setLearningTopicGenerating: (event: LearningTopicGeneratingEvent | null) => void
  reset: () => void
}

export const useChatStore = create<ChatState>()((set) => ({
  // Initial state
  chatId: null,
  messages: [],
  isSending: false,

  previewPanelOpen: false,
  resumeContent: null,
  originalResumeContent: null,
  selectedTemplate: 'modern',
  userDocumentId: null,
  isGeneratingRevision: false,
  isUpdatingPreview: false,
  previewJobTitle: undefined,
  previewCompany: undefined,
  previewPdfUrl: null,
  previewIsAgentTailored: false,
  previewLoading: false,

  scrollDistanceFromBottom: 0,
  scrollWasAtBottom: true,
  coachFeedbackToken: 0,
  coachAttentionActive: false,
  mobileCoachOpenToken: 0,
  coachInputFocusToken: 0,

  learningContent: null,
  learningPanelOpen: false,
  isLoadingLearning: false,
  learningError: null,
  learningTopicGenerating: null,

  // Actions
  setChatId: (id) => set({ chatId: id }),

  setMessages: (messagesOrUpdater) =>
    set((state) => ({
      messages:
        typeof messagesOrUpdater === 'function'
          ? messagesOrUpdater(state.messages)
          : messagesOrUpdater,
    })),

  addMessage: (message) => set((state) => ({ messages: [...state.messages, message] })),

  updateMessage: (id, updates) =>
    set((state) => ({
      messages: state.messages.map((msg) => (msg.id === id ? { ...msg, ...updates } : msg)),
    })),

  setIsSending: (sending) => set({ isSending: sending }),

  // Resume preview
  setPreviewPanelOpen: (open) => set({ previewPanelOpen: open }),
  setResumeContent: (content) => set({ resumeContent: content }),
  setOriginalResumeContent: (content) => set({ originalResumeContent: content }),
  setSelectedTemplate: (template) => set({ selectedTemplate: template }),
  setUserDocumentId: (id) =>
    set((state) => (state.userDocumentId === id ? state : { userDocumentId: id })),
  setIsGeneratingRevision: (generating) => set({ isGeneratingRevision: generating }),
  setIsUpdatingPreview: (updating) => set({ isUpdatingPreview: updating }),
  setPreviewJobContext: (jobTitle, company) =>
    set({ previewJobTitle: jobTitle, previewCompany: company }),
  setPreviewPdfUrl: (url) => set({ previewPdfUrl: url }),
  setPreviewIsAgentTailored: (tailored) => set({ previewIsAgentTailored: tailored }),
  setPreviewLoading: (loading) => set({ previewLoading: loading }),
  clearResumePreviewContext: () =>
    set({
      previewJobTitle: undefined,
      previewCompany: undefined,
      previewPdfUrl: null,
      previewIsAgentTailored: false,
      previewLoading: false,
    }),

  // Scroll position
  setScrollAnchor: (distanceFromBottom, wasAtBottom) =>
    set({ scrollDistanceFromBottom: distanceFromBottom, scrollWasAtBottom: wasAtBottom }),

  requestCoachFeedback: () =>
    set((state) => ({
      coachFeedbackToken: state.coachFeedbackToken + 1,
      coachAttentionActive: true,
    })),

  requestMobileCoachOpen: () =>
    set((state) => ({ mobileCoachOpenToken: state.mobileCoachOpenToken + 1 })),

  requestCoachInputFocus: () =>
    set((state) => ({ coachInputFocusToken: state.coachInputFocusToken + 1 })),

  clearCoachAttention: () => set({ coachAttentionActive: false }),

  // Learning panel
  setLearningContent: (contentOrUpdater) =>
    set((state) => ({
      learningContent:
        typeof contentOrUpdater === 'function'
          ? contentOrUpdater(state.learningContent)
          : contentOrUpdater,
    })),
  setLearningPanelOpen: (open) => set({ learningPanelOpen: open }),
  setIsLoadingLearning: (loading) => set({ isLoadingLearning: loading }),
  setLearningError: (error) => set({ learningError: error }),
  setLearningTopicGenerating: (event) => set({ learningTopicGenerating: event }),

  reset: () =>
    set({
      chatId: null,
      messages: [],
      isSending: false,
      scrollDistanceFromBottom: 0,
      scrollWasAtBottom: true,
      previewPanelOpen: false,
      resumeContent: null,
      originalResumeContent: null,
      selectedTemplate: 'modern',
      userDocumentId: null,
      isGeneratingRevision: false,
      isUpdatingPreview: false,
      previewJobTitle: undefined,
      previewCompany: undefined,
      previewPdfUrl: null,
      previewIsAgentTailored: false,
      previewLoading: false,
      learningContent: null,
      learningPanelOpen: false,
      isLoadingLearning: false,
      learningError: null,
      learningTopicGenerating: null,
    }),
}))
